import { useEffect, useRef } from 'react'

/**
 * Payment iframe ↔ parent message protocol. Ported byte-identical from
 * al_spare_parts/src/components/Pages/Checkout/Body.js, with one safety
 * addition: an event.origin allowlist. The legacy code did not validate
 * origin, so any iframe on the page (or a malicious browser extension)
 * could fire AL_ECOM_CLEAR_CART.
 *
 * The allowlist is derived from the paymentURL returned by /v1/sp/order — we
 * pass it in here so the parent can reject messages from anywhere else.
 *
 * Handlers are stored in a ref so callers don't need to memoise the object —
 * the message listener is attached once per paymentUrl and reads the latest
 * handlers from the ref on every message.
 */

export const CLEAR_CART_COMMAND = 'AL_ECOM_CLEAR_CART'
export const CHANGE_TITLE_THANK_YOU = 'AL_ECOM_CHANGE_TITLE_THANK_YOU'
export const CHANGE_TITLE_SORRY = 'AL_ECOM_CHANGE_TITLE_SORRY'

export type PaymentTitle = 'Payment' | 'Thank you' | 'Sorry'

export type PaymentHandlers = {
  onClearCart: () => void
  onTitleChange: (next: PaymentTitle) => void
}

export function usePaymentMessages(paymentUrl: string | null, handlers: PaymentHandlers): void {
  const handlersRef = useRef(handlers)
  useEffect(() => {
    handlersRef.current = handlers
  })

  useEffect(() => {
    if (!paymentUrl) return
    let expectedOrigin: string
    try {
      expectedOrigin = new URL(paymentUrl).origin
    } catch {
      // Malformed URL — we can't establish a trusted origin, so we must NOT
      // listen at all. Accepting any origin would let any iframe / extension
      // fire AL_ECOM_CLEAR_CART. (SAP should never return a bad URL; this is
      // the safe failure mode if it ever does.)
      return
    }

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== expectedOrigin) return
      if (typeof e.data !== 'string') return

      const h = handlersRef.current
      switch (e.data) {
        case CLEAR_CART_COMMAND:
          h.onClearCart()
          break
        case CHANGE_TITLE_THANK_YOU:
          h.onTitleChange('Thank you')
          break
        case CHANGE_TITLE_SORRY:
          h.onTitleChange('Sorry')
          break
        default:
          break
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [paymentUrl])
}
